import {share} from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import {HttpClient} from './http.service';
import {Product} from '../classes/product';
import {ShoppingCartLine} from '../classes/shoppingcartline';
import {EMPTY, Observable, Subject} from 'rxjs';
import {BaseService} from './base.service';
import {ApplicationService} from './application.service';
import {ActiveOrder} from '../classes/active.order';
import {UserService} from './user.service';
import {Logger} from '../logging/default-log.service';
import {ActiveOrderService} from './active.order.service';
import {ConfigurationHelper} from '../helper/configuration.helper';
import {TagManagerService} from './tag.manager.service';
import {CookieService} from './cookie.service';
import {SourceMetrics} from '../classes/source.metrics';
import {PlantscapingService} from './plantscaping.service';
import {ProductOrderData} from '../classes/product.order.data';
import {ProductTypes} from '../classes/product.types.enum';
import {ProductService} from './product.service';

@Injectable()
export class ShoppingCartService extends BaseService {
	protected logger: Logger;
	private http = inject(HttpClient);
	private applicationService = inject(ApplicationService);
	private activeOrderService = inject(ActiveOrderService);
	private tagManagerService = inject(TagManagerService);
	private cookieService = inject(CookieService);
	private userService = inject(UserService);
	private plantScapingService = inject(PlantscapingService);
	private productService = inject(ProductService);

	private shoppingCartSubject = new Subject<ShoppingCartLine[]>();
	private shoppingCartObservable = this.shoppingCartSubject.asObservable();

	constructor() {
		const logger = inject(Logger);

		super(logger);
		this.logger = logger;
		const userService = this.userService;


		if (!userService.getUser()) {
			return;
		}

		this.activeOrderService.selectedOrderChangedSubject.subscribe(
			(newActiveOrder) => {
				this.updateShoppingCartForActiveOrder(newActiveOrder);
			},
			error => this.logger.error('Failed to subscribe to order changed', error)
		);
	}

	private _activeShoppingCartLines: ShoppingCartLine[];

	get activeShoppingCartLines(): ShoppingCartLine[] {
		return this._activeShoppingCartLines;
	}

	public getShoppingCartObservable() {
		return this.shoppingCartObservable;
	}

	/**
	 * Check if the given shoppingCartLine can still be edited.
	 * It can be determined by:
	 * - The edit time is still in the future
	 * - The order/shoppingcartline has already been confirmed manually by the user
	 *
	 * @param shoppingCartLine
	 * @returns {boolean}
	 */
	isShoppingCartLineEditable(shoppingCartLine: ShoppingCartLine): boolean {
		const shoppingCartLineExpiryDate: Date =
			this.getShoppingCartLineExpiryDate(shoppingCartLine);

		if (shoppingCartLineExpiryDate === null) {
			return false;
		}

		if (shoppingCartLine.confirmed > 0 || shoppingCartLine.expired) {
			return false;
		}

		return new Date() < shoppingCartLineExpiryDate;
	}

	getShoppingCartLineExpiryDate(shoppingCartLine: ShoppingCartLine): Date {
		const inserted = shoppingCartLine.inserted;
		let waitTimeInSeconds = this.userService.getUserCartWaitTimeInSeconds();

		if (waitTimeInSeconds) {
			waitTimeInSeconds = waitTimeInSeconds * 1000;
			const expiryDate = inserted + waitTimeInSeconds;
			return new Date(expiryDate);
		}

		return null;
	}

	getShoppingCart(
		addressCode: string,
		shipmentDate: number,
		sorting?: string,
		sortingDirection?: string
	): Observable<ShoppingCartLine[]> {
		const params: Array<string> = [];

		params.push(`shipToCode=${encodeURIComponent(addressCode)}`);
		params.push(`shipmentDate=${shipmentDate}`);

		if (sorting && sortingDirection) {
			params.push(`sorting=${sorting}`);
			params.push(`sortingDirection=${sortingDirection}`);
		}

		const paramsJoined: string = params.join('&');

		return this.getLinesForSpecificCart(paramsJoined);
	}

	getPendingOrderLines(
		addressCode: string,
		shipmentDate: number,
		sorting: string,
		sortingDirection: string
	): Observable<ShoppingCartLine[]> {
		const params: Array<string> = [];

		params.push(`shipToCode=${encodeURIComponent(addressCode)}`);
		params.push(`shipmentDate=${shipmentDate}`);

		if (sorting && sortingDirection) {
			params.push(`sorting=${sorting}`);
			params.push(`sortingDirection=${sortingDirection}`);
		}

		const paramsJoined: string = params.join('&');

		return this.getLinesForSpecificPendingOrder(paramsJoined);
	}

	updateShoppingCartForActiveOrder(newActiveOrder: ActiveOrder) {
		const params: Array<string> = [];

		if (newActiveOrder) {
			params.push(`shipToCode=${encodeURIComponent(newActiveOrder.shipToCode)}`);
			params.push(`shipmentDate=${newActiveOrder.shipmentDate}`);
		}

		const urlParams = params.join('&');

		this.getLinesForSpecificCart(urlParams).subscribe(
			(data) => {
				this._activeShoppingCartLines = data;
				this.shoppingCartSubject.next(this._activeShoppingCartLines);
			},
			(error) => {
				this.shoppingCartSubject.error(error);
				this.resetActiveShoppingCart();
			}
		);
	}

	showDeliveryDateModal(orderData: ProductOrderData, product: Product) {
		this.applicationService.openPopup(
			(order: ActiveOrder) => {
				let updateLocalShoppingCart = false;

				const selectedOrder = this.activeOrderService.selectedOrder;

				if (order === selectedOrder) {
					updateLocalShoppingCart = true;
				}

				const $productOrderDone = this.addProductToOrder(orderData, order, updateLocalShoppingCart);

				// Can be null if the user had to select a (new) active order.
				if ($productOrderDone == null) {
					return;
				}

				$productOrderDone.subscribe(
					() => {
						this.productService.triggerProductAddedToCartEvent(product);
						this.applicationService.closePopup();
					},
					() => {
						// Also close the 'start new order popup' if the ordering failed.
						// The active order is successfully set, only the shopping cart is not updated.
						this.applicationService.closePopup();
					}
				);
			},
			() => {
			},
			true,
			orderData
		);
	}

	/**
	 * Add a product to the order.
	 * @param orderData with product to add to the order.
	 * @param order to add the product to.
	 * @param updateLocalShoppingCart if the local shopping cart should be updated. Default is true.
	 */
	addProductToOrder(orderData: ProductOrderData,
	                  order: ActiveOrder,
	                  updateLocalShoppingCart: boolean = true) {

		if (!order) {
			this.logger.error('Failed to add product to order. No active order selected.');
			return null;
		}

		if (orderData.buyAll) {
			return this.buyAll(orderData, order, updateLocalShoppingCart);
		}

		return this.saveOrderProduct(orderData, order, updateLocalShoppingCart);
	}

	/**
	 * Order a product. If there's no active order selected yet,
	 * the user is first to set an active or new order.
	 * After selecting it, the product is added to the new active order.
	 *
	 * @param product
	 * @param quantity
	 * @param buyAll
	 */
	orderProduct(product: Product, quantity: number | null, buyAll: boolean) {
		const orderData = this.toProductOrderData(product, quantity, buyAll);

		const selectedOrder = this.activeOrderService.selectedOrder;

		if (!selectedOrder) {
			this.showDeliveryDateModal(orderData, product);
			return null;
		}

		// Always show the delivery date modal when a product is ordered for a later date.
		if (product.isAvailableLater ||
			product.isStockAvailableLater ||
			(product.isDirectlyAvailable && quantity > this.getAvailableQuantity(product))) {

			this.showDeliveryDateModal(orderData, product);
			return null;
		}

		return this.addProductToOrder(orderData, selectedOrder);
	}

	getSourceMetricCookieValueAsObject(): SourceMetrics | null {
		const initialTrafficSourcesCookie = this.cookieService.getCookieByName('initialTrafficSource');

		if (!initialTrafficSourcesCookie) {
			return null;
		}

		return initialTrafficSourcesCookie
			.split('|')
			.filter(Boolean)
			.reduce((acc, next) => {
				const [key, value] = next.split('=');
				acc[key] = value;
				return acc;
			}, {});
	}

	removeProduct(shoppingCartLine: ShoppingCartLine) {
		const url = ConfigurationHelper.getWebshopUrl('/rest/shoppingcart/remove');

		this.http
			.post(
				url,
				JSON.stringify({
					priceListLineNo: shoppingCartLine.priceListLineNo,
					priceListNo: shoppingCartLine.priceListNo,
					type: shoppingCartLine.type,
					shipToCode: shoppingCartLine.shipToCode,
					shipmentDate: shoppingCartLine.shipmentDate,
					shoppingCartNo: shoppingCartLine.shoppingCartNo,
					shoppingCartMode: shoppingCartLine.shoppingCartMode
				})
			)
			.subscribe(
				() => {
					this.tagManagerService.trackRemoveFromCart(shoppingCartLine);
					this.notifyShoppingCartChange(
						shoppingCartLine.shipToCode,
						shoppingCartLine.shipmentDate
					);
				},
				error => this.handleError(error)
			);
	}

	changeQuantity(shoppingCartLine: ShoppingCartLine) {
		const url = ConfigurationHelper.getWebshopUrl('/rest/shoppingcart/changeQuantity');

		this.http
			.post(
				url,
				JSON.stringify({
					priceListLineNo: shoppingCartLine.priceListLineNo,
					priceListNo: shoppingCartLine.priceListNo,
					quantity: shoppingCartLine.quantity,
					type: shoppingCartLine.type,
					shipToCode: shoppingCartLine.shipToCode,
					shipmentDate: shoppingCartLine.shipmentDate,
					shoppingCartNo: shoppingCartLine.shoppingCartNo,
					shoppingCartMode: shoppingCartLine.shoppingCartMode
				})
			)
			.subscribe(
				() =>
					this.notifyShoppingCartChange(
						shoppingCartLine.shipToCode,
						shoppingCartLine.shipmentDate
					),
				error => this.handleError(error)
			);
	}

	/**
	 * Notifies the listeners that the shopping cart has changed
	 */
	public notifyShoppingCartChange(addressCode, shipmentDate) {
		this.getShoppingCart(addressCode, shipmentDate, null, null).subscribe(
			(lines) => {
				this._activeShoppingCartLines = lines;
				this.shoppingCartSubject.next(lines);
			},
			error => this.handleError(error)
		);
	}

	getNumberOfItemsInOrderForProduct(product: Product): number {
		if (this._activeShoppingCartLines) {
			const activeLine = this._activeShoppingCartLines.find(line =>
				line.priceListLineNo === product.priceListLineNo &&
				line.priceListNo === product.priceListNo);

			if (
				activeLine !== undefined &&
				(activeLine.shoppingCartMode ||
					this.isShoppingCartLineEditable(activeLine))
			) {
				return activeLine.quantity;
			}
		}

		return 0;
	}

	/**
	 * Adds a product with all current stock to the order.
	 *
	 * @param orderData to send.
	 * @param order to add the product to.
	 * @param updateLocalShoppingCart if the local shopping cart should be updated.
	 */
	buyAll(orderData: ProductOrderData, order: ActiveOrder, updateLocalShoppingCart: boolean) {
		const url = ConfigurationHelper.getWebshopUrl('/rest/shoppingcart/buyall');
		const sourceMetrics = this.getSourceMetricCookieValueAsObject();

		const $xhr = this.http
			.post(
				url,
				JSON.stringify({
					priceListLineNo: orderData.priceListLineNo,
					priceListNo: orderData.priceListNo,
					type: orderData.type,
					shipmentDate: order.shipmentDate,
					shipToCode: encodeURIComponent(order.shipToCode),
					shoppingCartMode: !this.userService.isUseFastCheckout(),
					source: sourceMetrics?.utmcsr ?? null,
					medium: sourceMetrics?.utmcmd ?? null,
					campaign: sourceMetrics?.utmccn ?? null,
					campaignContent: sourceMetrics?.utmcct ?? null,
					campaignTerm: sourceMetrics?.utmctr ?? null
				})
			)
			.pipe(share());

		$xhr.subscribe(
			() => {
				if (updateLocalShoppingCart) {
					this.notifyShoppingCartChange(order.shipToCode, order.shipmentDate);
				}
			},
			error => this.handleError(error)
		);

		return $xhr;
	}

	/**
	 * Calls the backend to confirm the order with the given shipmentDate/shipToCode combination
	 * @param shipmentDate
	 * @param shipToCode
	 */
	confirmAll(shipmentDate?: number, shipToCode?: string): Observable<boolean> {
		if (!shipmentDate || !shipToCode) {
			this.handleError('Failed to confirm all order lines. We could not verify the active order data.');
			return EMPTY;
		}

		const url = ConfigurationHelper.getWebshopUrl('/rest/shoppingcart/confirmall');

		return this.http.post<boolean>(
			url,
			JSON.stringify({
				shipmentDate,
				shipToCode: encodeURIComponent(shipToCode)
			})
		);
	}

	private resetActiveShoppingCart(): void {
		this._activeShoppingCartLines = null;
	}

	private getLinesForSpecificCart(params: string): Observable<ShoppingCartLine[]> {
		let url = ConfigurationHelper.getWebshopUrl('/rest/shoppingcart');

		if (params) {
			url += '?' + params;
		}

		return this.http.get<ShoppingCartLine[]>(url).pipe(share());
	}

	private getLinesForSpecificPendingOrder(params: string): Observable<ShoppingCartLine[]> {
		let url = ConfigurationHelper.getWebshopUrl('/rest/pendingorderdetails');

		if (params) {
			url += '?' + params;
		}

		return this.http.get(url);
	}

	private toProductOrderData(product: Product, orderQuantity: number, buyAll: boolean): ProductOrderData {
		const orderData = new ProductOrderData();
		orderData.availableLaterDate = product.availableLaterDate;
		orderData.priceListLineNo = product.priceListLineNo;
		orderData.priceListNo = product.priceListNo;
		orderData.type = product.type;
		orderData.quantity = orderQuantity;
		orderData.buyAll = buyAll;

		// Override values if product is directly available but more than the current stock is ordered.
		if (product.isDirectlyAvailable && orderQuantity > this.getAvailableQuantity(product)) {
			orderData.priceListNo = product.availableLaterPriceListNo;
			orderData.priceListLineNo = product.availableLaterPriceListLineNo;
			orderData.type = ProductTypes.ASSORTMENT;
		}

		return orderData;
	};

	/**
	 * this is the call to the server which adds the product
	 *
	 * @param orderData to send.
	 * @param order to add the product to.
	 * @param updateLocalShoppingCart if the local shopping cart should be updated.
	 */
	private saveOrderProduct(orderData: ProductOrderData,
	                         order: ActiveOrder,
	                         updateLocalShoppingCart: boolean) {

		const url = ConfigurationHelper.getWebshopUrl('/rest/shoppingcart/order');
		const sourceMetrics = this.getSourceMetricCookieValueAsObject();

		const $xhr = this.http
			.post(
				url,
				JSON.stringify({
					priceListLineNo: orderData.priceListLineNo,
					priceListNo: orderData.priceListNo,
					type: orderData.type,
					quantity: orderData.quantity,
					shipmentDate: order.shipmentDate,
					shipToCode: encodeURIComponent(order.shipToCode),
					shoppingCartMode: !this.userService.isUseFastCheckout(),
					source: sourceMetrics?.utmcsr ?? null,
					medium: sourceMetrics?.utmcmd ?? null,
					campaign: sourceMetrics?.utmccn ?? null,
					campaignContent: sourceMetrics?.utmcct ?? null,
					campaignTerm: sourceMetrics?.utmctr ?? null,
					trackPlantscaping: this.plantScapingService.getPlantscapingFromSession()
				})
			)
			.pipe(share());

		$xhr.subscribe(
			() => {
				if (updateLocalShoppingCart) {
					this.notifyShoppingCartChange(order.shipToCode, order.shipmentDate);
				}
			},
			error => this.logger.error('Failed to add product to order', error)
		);

		return $xhr;
	}

	private getAvailableQuantity(product: Product): number {
		const amountAvailable = product.availableQuantity - this.getNumberOfItemsInOrderForProduct(product);
		return amountAvailable > 0 ? amountAvailable : 0;
	}
}
